import {
  legalDocuments,
  parseFrontmatter,
  type LegalDocument,
} from "@scryerlabs/legal";

import termsMd from "@scryerlabs/legal/content/terms.md?raw";
import privacyMd from "@scryerlabs/legal/content/privacy.md?raw";
import disclosuresMd from "@scryerlabs/legal/content/disclosures.md?raw";

/**
 * The published legal documents, as rendered by this portal.
 *
 * The wording comes from @scryerlabs/legal so the portal and the brochure site
 * cannot say different things. Everything visual is this app's own: the brochure
 * renders the same three documents through its card styling and looks nothing
 * like these pages.
 *
 * The three imports are written out rather than globbed. `?raw` is a Vite
 * feature, and import.meta.glob does not reach into a dependency, so the files
 * have to be named — which is no worse than it looks: the package's own
 * validator fails CI if its registry and its content/ directory disagree, so a
 * fourth document cannot appear without someone editing that registry, and the
 * check below turns a missed import here into a startup error rather than a
 * silently absent page.
 */
/** The one document that is agreed to rather than merely shown. */
const TERMS_SLUG = "terms";

const SOURCES: Record<string, string> = {
  terms: termsMd,
  privacy: privacyMd,
  disclosures: disclosuresMd,
};

export interface LegalPageContent extends LegalDocument {
  /** Heading shown at the top of the page. */
  heading: string;
  /** When the document's substance last changed, e.g. "August 2026". */
  lastUpdated: string;
  /** One-line description of the document. */
  summary: string;
  /** The Markdown, frontmatter removed. */
  body: string;
}

function build(): Map<string, LegalPageContent> {
  const built = new Map<string, LegalPageContent>();

  for (const doc of legalDocuments) {
    const source = SOURCES[doc.slug];
    if (!source) {
      throw new Error(
        `@scryerlabs/legal lists "${doc.slug}" but this portal has no import for it. ` +
          `Add one to SOURCES in legal-content.ts.`
      );
    }

    const { data, body } = parseFrontmatter(source);
    built.set(doc.slug, {
      ...doc,
      heading: data.title ?? doc.title,
      lastUpdated: data.lastUpdated ?? "",
      summary: data.summary ?? "",
      body,
    });
  }

  // The gate compares a customer's stored acceptance against the Terms' lastUpdated and posts every
  // document's version to the API, which rejects a blank one. A package release that dropped the
  // Terms or shipped them without a lastUpdated would therefore leave every customer stuck on the
  // acceptance dialog, unable to enter the portal and unable to get past it — a total lockout, with
  // nothing in this app to show why. Failing here instead turns that into a blank app and a console
  // error the first time anyone runs it, which is caught in dev or CI rather than in production.
  const terms = built.get(TERMS_SLUG);

  if (!terms) {
    throw new Error(
      `@scryerlabs/legal publishes no "${TERMS_SLUG}" document. The portal's acceptance gate has ` +
        `nothing to ask a customer to accept.`
    );
  }

  if (!terms.lastUpdated.trim()) {
    throw new Error(
      `The "${TERMS_SLUG}" document from @scryerlabs/legal has no lastUpdated in its frontmatter. ` +
        `That value is the version customers accept against, and the API rejects a blank one.`
    );
  }

  return built;
}

const documents = build();

export function getLegalPage(slug: string | undefined): LegalPageContent | undefined {
  return slug ? documents.get(slug) : undefined;
}

/** In footer order, from the package's registry rather than a list held here. */
export const legalPages: LegalPageContent[] = legalDocuments
  .map((doc) => documents.get(doc.slug))
  .filter((doc): doc is LegalPageContent => Boolean(doc));

/**
 * The slug of the document served at an in-app path such as `/privacy`, or
 * undefined where the path is not one of the legal documents. Reads the routes
 * from the package rather than matching on strings, so a document whose path
 * changes there does not quietly stop resolving here.
 */
export function getLegalPathSlug(path: string): string | undefined {
  return legalDocuments.find((doc) => doc.path === path)?.slug;
}

/** One document as the acceptance gate presents it. */
export interface LegalAcceptanceDocument {
  slug: string;
  /** Heading shown on the panel. */
  title: string;
  version: string;
  body: string;
  /**
   * Whether the customer is being asked to agree to this document, or only being
   * shown it.
   *
   * Only the Terms are agreed to. A privacy notice is information we are obliged
   * to provide rather than terms someone accepts, and treating it as accepted
   * would have us assert consent as our legal basis when the policy itself relies
   * on performance of a contract and legitimate interests. Disclosures are
   * acknowledged. The single checkbox is worded to match, and this flag is what
   * the stored record carries.
   */
  agreed: boolean;
}

/**
 * Every document the acceptance dialog puts to a customer, in the package's own
 * order, each at the version currently published.
 */
export const legalAcceptanceDocuments: LegalAcceptanceDocument[] = legalPages.map((doc) => ({
  slug: doc.slug,
  title: doc.heading,
  version: doc.lastUpdated,
  body: doc.body,
  agreed: doc.slug === TERMS_SLUG,
}));

/**
 * The version of the Terms currently published — the value the provisioning gate
 * compares a customer's stored acceptance against. Never blank: `build()` refuses
 * to produce a document set without it.
 */
export const currentTermsVersion: string = documents.get(TERMS_SLUG)!.lastUpdated;
